import { computeRotation, decomposeRotation, downloadGLTF } from "@novorender/api";
import { mat3, mat4, quat, ReadonlyMat4, ReadonlyVec3, vec3 } from "gl-matrix";
import { useCallback, useEffect, useRef, useState } from "react";

import { Events, track } from "apis/procore";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useCameraState } from "contexts/cameraState";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useLazyHighlighted } from "contexts/highlighted";
import { getCameraDir } from "features/engine2D/utils";
import {
    CameraType,
    renderActions,
    selectAxisSphereConstraints,
    selectCameraType,
    selectMainObject,
} from "features/render";
import { flipCADToGLQuat } from "features/render/utils";
import { useAbortController } from "hooks/useAbortController";
import { selectConfig } from "slices/explorer";
import { telemetry } from "telemetry";
import { degToRad, radToDeg } from "utils/math";
import { objIdsToTotalBoundingSphere } from "utils/objectData";

import {
    AXIS_SPHERE_CAMERA_DIRECTIONS,
    CAMERA_FLY_TIME,
    computeNewCameraPosition,
    computeTargetRotation,
} from "./cameraNavigation";

const DEFAULT_SIZE = 160; // px
const DRAG_THRESHOLD = 4; // px
const DRAG_SENSITIVITY = 0.005; // radians per pixel

interface ParsedPrimitive {
    name?: string;
    positions: Float32Array;
    normals: Float32Array | null;
    indices: Uint32Array;
    baseColor: readonly [number, number, number, number];
    transform: ReadonlyMat4;
}

function extractFloat32Array(source: BufferSource): Float32Array {
    if (source instanceof Float32Array) {
        return source.slice();
    }
    if (source instanceof ArrayBuffer) {
        return new Float32Array(source);
    }
    // It's an ArrayBufferView, create a copy from its underlying buffer
    return new Float32Array(source.buffer.slice(source.byteOffset, source.byteOffset + source.byteLength));
}

function extractUint32Array(source: Uint32Array | Uint16Array | Uint8Array): Uint32Array {
    if (source instanceof Uint32Array) {
        return source.slice();
    }
    // Convert Uint16Array or Uint8Array to Uint32Array
    return new Uint32Array(source);
}

// This might be tailored a bit for particular GLB file, so if file changes - some adjustments might be needed
async function parseGLB(url: URL): Promise<ParsedPrimitive[]> {
    const gltf = await downloadGLTF(url, undefined, undefined, "glb");
    if (!gltf || gltf.length === 0) {
        console.warn("No objects found in GLB file");
        return [];
    }
    // Infer the scale factor needed to normalize the largest extent of the model to size 1.

    // First, flatten all positions in all primitives to get an overall bounding box.
    // This is technically inefficient if called many times but simple for one-time asset load.
    const min = vec3.fromValues(Infinity, Infinity, Infinity);
    const max = vec3.fromValues(-Infinity, -Infinity, -Infinity);

    for (const obj of gltf) {
        const attrs = obj.mesh.primitives[0].geometry.attributes;
        const positions = extractFloat32Array(attrs.position.buffer);
        for (let k = 0; k < positions.length; k += 3) {
            min[0] = Math.min(min[0], positions[k]);
            min[1] = Math.min(min[1], positions[k + 1]);
            min[2] = Math.min(min[2], positions[k + 2]);
            max[0] = Math.max(max[0], positions[k]);
            max[1] = Math.max(max[1], positions[k + 1]);
            max[2] = Math.max(max[2], positions[k + 2]);
        }
    }

    const size = vec3.subtract(vec3.create(), max, min);
    const maxExtent = Math.max(size[0], size[1], size[2]);
    // Avoid division by zero, fallback to 1 if needed
    const uniformScale = maxExtent > 0 ? 1 / maxExtent : 1;

    return gltf.map((obj) => {
        const attrs = obj.mesh.primitives[0].geometry.attributes;
        const { position, rotation: originalRotation } = obj.instances[0];
        // Clone the rotation to avoid mutating the original
        const rotation = originalRotation ? quat.clone(originalRotation) : quat.create();
        quat.rotateX(rotation, rotation, -Math.PI);
        const transform = mat4.fromRotationTranslationScale(
            mat4.create(),
            rotation,
            position,
            vec3.fromValues(uniformScale, uniformScale, uniformScale),
        );

        const geometry = obj.mesh.primitives[0].geometry;
        let indices: Uint32Array;
        if (typeof geometry.indices === "number") {
            // We do not support numeric indices here; this would create an invalid zero-filled index buffer.
            throw new Error(
                `Unexpected numeric geometry.indices (${geometry.indices}) for mesh "${obj.mesh.name}". ` +
                    "Indexed meshes are expected to provide an index buffer.",
            );
        } else {
            indices = extractUint32Array(geometry.indices);
        }

        return {
            name: obj.mesh.name,
            positions: extractFloat32Array(attrs.position.buffer),
            normals: attrs.normal ? extractFloat32Array(attrs.normal.buffer) : null,
            indices,
            baseColor: obj.mesh.primitives[0].material.baseColorFactor ?? [1, 1, 1, 1],
            transform,
        };
    });
}

function createShader(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader {
    const shader = gl.createShader(type)!;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        const info = gl.getShaderInfoLog(shader);
        gl.deleteShader(shader);
        throw new Error(`Shader compile error: ${info}`);
    }
    return shader;
}

function createProgram(
    gl: WebGL2RenderingContext,
    vertexShader: WebGLShader,
    fragmentShader: WebGLShader,
): WebGLProgram {
    const program = gl.createProgram()!;
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        const info = gl.getProgramInfoLog(program);
        gl.deleteProgram(program);
        throw new Error(`Program link error: ${info}`);
    }
    return program;
}

const vertexShaderSource = `#version 300 es
precision highp float;

in vec3 a_position;
in vec3 a_normal;

uniform mat4 u_mvp;
uniform mat4 u_modelView;
uniform mat3 u_normalMatrix;

out vec3 v_normal;
out vec3 v_viewPos;

void main() {
    gl_Position = u_mvp * vec4(a_position, 1.0);
    v_normal = normalize(u_normalMatrix * a_normal);
    v_viewPos = (u_modelView * vec4(a_position, 1.0)).xyz;
}
`;

const fragmentShaderSource = `#version 300 es
precision highp float;

in vec3 v_normal;
in vec3 v_viewPos;
uniform vec4 u_baseColor;
uniform vec4 u_highlight;

out vec4 fragColor;

void main() {
    vec3 color = u_baseColor.rgb;
    
    // Override color if highlight is set (hover or active camera direction)
    color = mix(color, u_highlight.rgb, u_highlight.a);
    
    vec3 normal = normalize(v_normal);
    vec3 viewDir = normalize(-v_viewPos);
    vec3 lightDir = normalize(vec3(0.5, 0.7, 1.0));
    
    // Ambient
    float ambient = 0.4;
    
    // Diffuse
    float diff = max(dot(normal, lightDir), 0.0);
    
    vec3 result = color * (ambient + (1.0 - ambient) * diff);
    
    fragColor = vec4(result, 1.0);
}
`;

// Picking shader - outputs mesh ID as color
const pickingVertexShaderSource = `#version 300 es
precision highp float;

in vec3 a_position;
uniform mat4 u_mvp;

void main() {
    gl_Position = u_mvp * vec4(a_position, 1.0);
}
`;

const pickingFragmentShaderSource = `#version 300 es
precision highp float;

uniform int u_meshId;
out vec4 fragColor;

void main() {
    // Encode mesh ID in RGB (supports up to 256 meshes with just R channel)
    fragColor = vec4(float(u_meshId) / 255.0, 0.0, 0.0, 1.0);
}
`;

interface PrimitiveGPUData {
    vao: WebGLVertexArrayObject;
    name?: string;
    indexCount: number;
    baseColor: readonly [number, number, number, number];
    transform: ReadonlyMat4;
    meshIndex: number;
}

interface DragState {
    startClientX: number;
    startClientY: number;
    initialRotation: quat;
    initialPosition: vec3;
    orbitPoint: vec3 | null;
    orbitPointResolved: boolean;
    isDragging: boolean;
}

export function AxisSphere({ size = DEFAULT_SIZE }: { size?: number } = {}) {
    const {
        state: { view, canvas: mainCanvas, db, scene },
    } = useExplorerGlobals();
    const highlighted = useLazyHighlighted();
    const mainObject = useAppSelector(selectMainObject);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const renderRef = useRef<(() => void) | null>(null);
    const pickDirtyRef = useRef(true);
    const pickRef = useRef<((x: number, y: number) => { meshIndex: number; partName?: string } | null) | null>(null);
    const hoveredMeshPartNameRef = useRef<string | null>(null);
    const [isHoveringClickable, setIsHoveringClickable] = useState(false);
    const [isPointerDown, setIsPointerDown] = useState(false);
    const [dpr, setDpr] = useState(() => window.devicePixelRatio || 1);
    const cameraType = useAppSelector(selectCameraType);
    const axisSphereConstraints = useAppSelector(selectAxisSphereConstraints);
    const cameraState = useCameraState();
    const cameraStateRef = useRef(cameraState);
    cameraStateRef.current = cameraState;

    const clickedPartNameRef = useRef<string | null>(null);
    const clickedPartHighlightTimerRef = useRef<NodeJS.Timeout | null>(null);

    const dragStateRef = useRef<DragState | null>(null);
    const [isDragging, setIsDragging] = useState(false);

    const assetsUrl = useAppSelector(selectConfig).assetsUrl;

    const glbPromiseRef = useRef<Promise<ParsedPrimitive[]> | null>(null);

    const [abortController, abort] = useAbortController();

    const dispatch = useAppDispatch();

    // Disable pointer events on the sphere while dragging elsewhere,
    // so camera rotation drags don't trigger hover highlights on the sphere.
    useEffect(() => {
        const handlePointerDown = (e: PointerEvent) => {
            if (canvasRef.current?.contains(e.target as Node)) {
                return;
            }
            setIsPointerDown(true);
            setIsHoveringClickable(false);
            if (hoveredMeshPartNameRef.current !== null) {
                hoveredMeshPartNameRef.current = null;
                renderRef.current?.();
            }
        };
        const endGlobalPointer = () => {
            setIsPointerDown(false);
            if (dragStateRef.current) {
                dragStateRef.current = null;
                setIsDragging(false);
            }
        };
        window.addEventListener("pointerdown", handlePointerDown);
        window.addEventListener("pointerup", endGlobalPointer);
        window.addEventListener("pointercancel", endGlobalPointer);
        return () => {
            window.removeEventListener("pointerdown", handlePointerDown);
            window.removeEventListener("pointerup", endGlobalPointer);
            window.removeEventListener("pointercancel", endGlobalPointer);
        };
    }, []);

    // Listen for devicePixelRatio changes (display switch, browser zoom)
    useEffect(() => {
        const mql = window.matchMedia(`(resolution: ${dpr}dppx)`);
        const onChange = () => setDpr(window.devicePixelRatio || 1);
        mql.addEventListener("change", onChange, { once: true });
        return () => mql.removeEventListener("change", onChange);
    }, [dpr]);

    useEffect(() => {
        pickDirtyRef.current = true;
        renderRef.current?.();
    }, [cameraState, dpr]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !view) {
            return;
        }

        const gl = canvas.getContext("webgl2", {
            alpha: true,
            premultipliedAlpha: false,
            antialias: true,
        });

        if (!gl) {
            console.error("WebGL2 not supported");
            return;
        }

        let program: WebGLProgram | null = null;
        let programLocs: {
            mvp: WebGLUniformLocation | null;
            modelView: WebGLUniformLocation | null;
            normalMatrix: WebGLUniformLocation | null;
            baseColor: WebGLUniformLocation | null;
            highlight: WebGLUniformLocation | null;
        } | null = null;
        let pickingProgram: WebGLProgram | null = null;
        let pickingLocs: {
            mvp: WebGLUniformLocation | null;
            meshId: WebGLUniformLocation | null;
        } | null = null;
        let pickingFramebuffer: WebGLFramebuffer | null = null;
        let pickingTexture: WebGLTexture | null = null;
        let pickingDepthBuffer: WebGLRenderbuffer | null = null;
        let pickingFbWidth = 0;
        let pickingFbHeight = 0;
        pickDirtyRef.current = true;
        const gpuData: PrimitiveGPUData[] = [];
        const buffers: WebGLBuffer[] = [];
        const initAbortController = new AbortController();

        const resizePickingFramebuffer = (width: number, height: number) => {
            if (!pickingFramebuffer || !pickingTexture || !pickingDepthBuffer) {
                return;
            }
            gl.bindFramebuffer(gl.FRAMEBUFFER, pickingFramebuffer);

            gl.bindTexture(gl.TEXTURE_2D, pickingTexture);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
            gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, pickingTexture, 0);

            gl.bindRenderbuffer(gl.RENDERBUFFER, pickingDepthBuffer);
            gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT16, width, height);
            gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, pickingDepthBuffer);

            gl.bindFramebuffer(gl.FRAMEBUFFER, null);

            pickingFbWidth = width;
            pickingFbHeight = height;
        };

        const init = async () => {
            try {
                const axisSphereUrl = new URL(`${assetsUrl}/axis-sphere/axis_sphere.glb`);
                // Add version to the URL to bust caching on new app release.
                // This version is not perfect, because it does not account for changes to the axis sphere model itself,
                // but axis sphere changes are part of the release where other stuff is included which will trigger a new version.
                // Azure will ignore this query parameter.
                axisSphereUrl.searchParams.set("v", __LIB_VERSION__);
                const glbPromise = glbPromiseRef.current ?? parseGLB(axisSphereUrl);
                glbPromiseRef.current = glbPromise;
                const meshes = await glbPromise;
                if (initAbortController.signal.aborted) {
                    return;
                }

                // Main shader
                const vs = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
                const fs = createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource);
                program = createProgram(gl, vs, fs);
                // Delete shader objects after linking - they're no longer needed and this prevents GPU memory leaks
                gl.deleteShader(vs);
                gl.deleteShader(fs);
                programLocs = {
                    mvp: gl.getUniformLocation(program, "u_mvp"),
                    modelView: gl.getUniformLocation(program, "u_modelView"),
                    normalMatrix: gl.getUniformLocation(program, "u_normalMatrix"),
                    baseColor: gl.getUniformLocation(program, "u_baseColor"),
                    highlight: gl.getUniformLocation(program, "u_highlight"),
                };

                // Picking shader
                const pickVs = createShader(gl, gl.VERTEX_SHADER, pickingVertexShaderSource);
                const pickFs = createShader(gl, gl.FRAGMENT_SHADER, pickingFragmentShaderSource);
                pickingProgram = createProgram(gl, pickVs, pickFs);
                gl.deleteShader(pickVs);
                gl.deleteShader(pickFs);
                pickingLocs = {
                    mvp: gl.getUniformLocation(pickingProgram, "u_mvp"),
                    meshId: gl.getUniformLocation(pickingProgram, "u_meshId"),
                };

                // Create picking framebuffer
                pickingFramebuffer = gl.createFramebuffer();
                pickingTexture = gl.createTexture();
                pickingDepthBuffer = gl.createRenderbuffer();
                resizePickingFramebuffer(canvas.width, canvas.height);

                const posLoc = gl.getAttribLocation(program, "a_position");
                const normLoc = gl.getAttribLocation(program, "a_normal");

                for (let i = 0; i < meshes.length; i++) {
                    const mesh = meshes[i];
                    const vao = gl.createVertexArray()!;
                    gl.bindVertexArray(vao);

                    // Positions
                    const posBuf = gl.createBuffer()!;
                    gl.bindBuffer(gl.ARRAY_BUFFER, posBuf);
                    gl.bufferData(gl.ARRAY_BUFFER, mesh.positions, gl.STATIC_DRAW);
                    gl.enableVertexAttribArray(posLoc);
                    gl.vertexAttribPointer(posLoc, 3, gl.FLOAT, false, 0, 0);
                    buffers.push(posBuf);

                    // Normals
                    if (mesh.normals && normLoc !== -1) {
                        const normBuf = gl.createBuffer()!;
                        gl.bindBuffer(gl.ARRAY_BUFFER, normBuf);
                        gl.bufferData(gl.ARRAY_BUFFER, mesh.normals, gl.STATIC_DRAW);
                        gl.enableVertexAttribArray(normLoc);
                        gl.vertexAttribPointer(normLoc, 3, gl.FLOAT, false, 0, 0);
                        buffers.push(normBuf);
                    } else if (normLoc !== -1) {
                        gl.disableVertexAttribArray(normLoc);
                        gl.vertexAttrib3f(normLoc, 0, 1, 0);
                    }

                    // Indices
                    const idxBuf = gl.createBuffer()!;
                    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, idxBuf);
                    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, mesh.indices, gl.STATIC_DRAW);
                    buffers.push(idxBuf);

                    gl.bindVertexArray(null);

                    gpuData.push({
                        vao,
                        name: mesh.name,
                        indexCount: mesh.indices.length,
                        baseColor: mesh.baseColor,
                        transform: mesh.transform,
                        meshIndex: i + 1, // Use 1-based index (0 = background/no mesh)
                    });
                }

                render();
            } catch (err) {
                console.error("Failed to load axis sphere:", err);
                telemetry.trackCaughtException({
                    source: "axisSphere.AxisSphere.init",
                    category: "render",
                    error: err,
                    severity: "error",
                });
            }
        };

        const getViewProjection = () => {
            const rotation = cameraStateRef.current?.rotation ? flipCADToGLQuat(cameraStateRef.current.rotation) : null;
            if (!rotation) {
                return { projection: mat4.create(), view: mat4.create() };
            }
            const cameraDistance = 2.4;
            // Compute orthoSize to match the visible vertical height as perspective camera at cameraDistance.
            // For vertical fit, orthoSize = tan(fov/2) * distance
            const fovY = degToRad(30);
            const orthoHalfHeight = Math.tan(fovY / 2) * cameraDistance;
            const orthoSize = orthoHalfHeight;
            const projection = mat4.ortho(mat4.create(), -orthoSize, orthoSize, -orthoSize, orthoSize, 0.1, 100);
            const cameraDir = getCameraDir(rotation);
            const cameraUp = vec3.transformQuat(vec3.create(), vec3.fromValues(0, 1, 0), rotation);
            const eye = vec3.normalize(vec3.create(), cameraDir);
            vec3.scale(eye, eye, cameraDistance);
            vec3.negate(eye, eye);
            const v = mat4.lookAt(mat4.create(), eye, vec3.fromValues(0, 0, 0), cameraUp);
            return { projection, view: v };
        };

        const render = () => {
            if (!program || !programLocs) {
                return;
            }

            gl.viewport(0, 0, canvas.width, canvas.height);
            gl.clearColor(0, 0, 0, 0);
            gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
            gl.enable(gl.DEPTH_TEST);
            gl.disable(gl.CULL_FACE);

            gl.useProgram(program);

            const { projection, view } = getViewProjection();

            // Get camera forward direction for active highlight
            const camRot = cameraStateRef.current?.rotation;
            const camDir = camRot ? getCameraDir(camRot) : null;

            const modelView = mat4.create();
            const mvp = mat4.create();
            const normalMatrix = mat3.create();
            for (const data of gpuData) {
                mat4.multiply(modelView, view, data.transform);
                mat4.multiply(mvp, projection, modelView);
                mat3.normalFromMat4(normalMatrix, modelView);

                gl.uniformMatrix4fv(programLocs.mvp, false, mvp);
                gl.uniformMatrix4fv(programLocs.modelView, false, modelView);
                gl.uniformMatrix3fv(programLocs.normalMatrix, false, normalMatrix);
                gl.uniform4fv(programLocs.baseColor, data.baseColor);

                // Determine highlight color: hover darkens base, active shows light blue
                const partName = data.name && getPartName(data.name);
                const isHovered = hoveredMeshPartNameRef.current === partName;

                const dirInfo = partName ? AXIS_SPHERE_CAMERA_DIRECTIONS[partName] : undefined;
                const isText = `${partName}_text` === data.name;
                let isActive = false;
                const dir = dirInfo ? getCameraDir(dirInfo.rotation as quat) : null;
                if (clickedPartNameRef.current === partName) {
                    isActive = true;
                } else if (camDir && camRot && dirInfo && dir) {
                    if (partName === "part_top") {
                        // For top view - highlight regardless of rotation around Z axis
                        isActive = vec3.dot(camDir, dir) > 0.999;
                    } else {
                        isActive = quat.equals(camRot, dirInfo.rotation);
                    }
                }

                if (isActive) {
                    // Make specific color when aligned with camera
                    const color = isText ? [1, 1, 1, 1] : [0.12549019607843137, 0.4, 0.8745098039215686, 1];
                    gl.uniform4f(programLocs.highlight, color[0], color[1], color[2], color[3]);
                } else if (isHovered) {
                    // Make darker
                    gl.uniform4f(programLocs.highlight, 1, 1, 1, 0.4);
                } else {
                    gl.uniform4f(programLocs.highlight, 0, 0, 0, 0);
                }

                gl.bindVertexArray(data.vao);
                gl.drawElements(gl.TRIANGLES, data.indexCount, gl.UNSIGNED_INT, 0);
            }

            gl.bindVertexArray(null);
        };

        const pick = (x: number, y: number): { meshIndex: number; partName?: string } | null => {
            if (!pickingProgram || !pickingLocs || !pickingFramebuffer) {
                return null;
            }

            // Resize picking framebuffer if canvas dimensions changed (e.g. browser zoom, DPR change)
            if (canvas.width !== pickingFbWidth || canvas.height !== pickingFbHeight) {
                resizePickingFramebuffer(canvas.width, canvas.height);
                pickDirtyRef.current = true;
            }

            // Only re-render the pick buffer when the scene has changed (camera moved)
            if (pickDirtyRef.current) {
                gl.bindFramebuffer(gl.FRAMEBUFFER, pickingFramebuffer);
                gl.viewport(0, 0, canvas.width, canvas.height);
                gl.clearColor(0, 0, 0, 0);
                gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
                gl.enable(gl.DEPTH_TEST);
                gl.disable(gl.CULL_FACE);

                gl.useProgram(pickingProgram);

                const { projection, view } = getViewProjection();

                const modelView = mat4.create();
                const mvp = mat4.create();
                for (const data of gpuData) {
                    mat4.multiply(modelView, view, data.transform);
                    mat4.multiply(mvp, projection, modelView);

                    gl.uniformMatrix4fv(pickingLocs.mvp, false, mvp);
                    gl.uniform1i(pickingLocs.meshId, data.meshIndex);

                    gl.bindVertexArray(data.vao);
                    gl.drawElements(gl.TRIANGLES, data.indexCount, gl.UNSIGNED_INT, 0);
                }

                gl.bindVertexArray(null);
                pickDirtyRef.current = false;
            } else {
                gl.bindFramebuffer(gl.FRAMEBUFFER, pickingFramebuffer);
            }

            // Read pixel at click position (flip Y coordinate)
            const pixelX = Math.floor(x);
            const pixelY = canvas.height - Math.floor(y) - 1;
            const pixel = new Uint8Array(4);
            gl.readPixels(pixelX, pixelY, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, pixel);
            gl.bindFramebuffer(gl.FRAMEBUFFER, null);

            // Decode mesh ID from red channel
            const meshId = pixel[0];
            if (meshId === 0) {
                return null; // Background, no mesh
            }
            const arrayIndex = meshId - 1; // Convert back to 0-based array index
            const meshData = gpuData[arrayIndex];
            // Return the 1-based meshIndex for consistency with gpuData.meshIndex
            return meshData
                ? { meshIndex: meshData.meshIndex, partName: meshData.name ? getPartName(meshData.name) : undefined }
                : null;
        };

        renderRef.current = render;
        pickRef.current = pick;

        init();

        return () => {
            initAbortController.abort();
            for (const data of gpuData) {
                gl.deleteVertexArray(data.vao);
            }
            for (const buf of buffers) {
                gl.deleteBuffer(buf);
            }
            if (program) {
                gl.deleteProgram(program);
            }
            if (pickingProgram) {
                gl.deleteProgram(pickingProgram);
            }
            if (pickingFramebuffer) {
                gl.deleteFramebuffer(pickingFramebuffer);
            }
            if (pickingTexture) {
                gl.deleteTexture(pickingTexture);
            }
            if (pickingDepthBuffer) {
                gl.deleteRenderbuffer(pickingDepthBuffer);
            }
        };
    }, [assetsUrl, view]);

    const handlePointerDown = useCallback(
        async (e: React.PointerEvent<HTMLCanvasElement>) => {
            if (!view || !mainCanvas) {
                return;
            }

            const cameraState = cameraStateRef.current;
            if (!cameraState) {
                return;
            }

            // Only start drag for primary mouse button; allow touch/pen as-is.
            if (e.pointerType === "mouse" && e.button !== 0) {
                return;
            }
            e.currentTarget.setPointerCapture(e.pointerId);

            abort();
            const abortSignal = abortController.current.signal;

            const dragState: DragState = {
                startClientX: e.clientX,
                startClientY: e.clientY,
                initialRotation: cameraState.rotation,
                initialPosition: cameraState.position,
                orbitPoint: null,
                orbitPointResolved: false,
                isDragging: false,
            };
            dragStateRef.current = dragState;

            const mainCanvasRect = mainCanvas.getBoundingClientRect();
            const pickResult = await view.pick(mainCanvasRect.width / 2, mainCanvasRect.height / 2, {
                sampleDiscRadius: 200,
                searchType: "closestToPickCenter",
                async: true,
            });

            if (abortSignal.aborted) {
                return;
            }

            dragState.orbitPoint = pickResult?.position ?? null;
            dragState.orbitPointResolved = true;
        },
        [view, mainCanvas, abort, abortController],
    );

    // Update camera on mesh click
    const handleClick = useCallback(
        async (e: React.MouseEvent<HTMLCanvasElement> | React.PointerEvent<HTMLCanvasElement>) => {
            if (!pickRef.current || !view || !mainCanvas) {
                return;
            }

            const canvas = canvasRef.current;
            if (!canvas) {
                return;
            }

            if (clickedPartHighlightTimerRef.current) {
                clearTimeout(clickedPartHighlightTimerRef.current);
                clickedPartHighlightTimerRef.current = null;
            }
            clickedPartNameRef.current = null; // Reset clicked part name

            const rect = canvas.getBoundingClientRect();
            const x = (e.clientX - rect.left) * (canvas.width / rect.width);
            const y = (e.clientY - rect.top) * (canvas.height / rect.height);

            const pickResult = pickRef.current(x, y);
            const cameraState = cameraStateRef.current;
            if (pickResult !== null && cameraState) {
                const { partName } = pickResult;
                if (!partName) {
                    return;
                }

                const rotation = computeTargetRotation(partName, cameraState.rotation);
                if (!rotation) {
                    return;
                }

                // Block face clicks that fall outside the allowed elevation zone(s).
                const viewConstraint = axisSphereConstraints?.allowedViews;
                if (viewConstraint) {
                    const zenithFaces = ["part_n", "part_s", "part_e", "part_w", "part_top"];
                    const equatorialFaces = ["part_front", "part_back", "part_left", "part_right"];
                    const allowed =
                        (viewConstraint === "zenith" && zenithFaces.includes(partName)) ||
                        (viewConstraint === "equatorial" && equatorialFaces.includes(partName)) ||
                        (viewConstraint === "zenith+equatorial" &&
                            (zenithFaces.includes(partName) || equatorialFaces.includes(partName)));
                    if (!allowed) {
                        return;
                    }
                }

                abort();
                const abortSignal = abortController.current.signal;

                // When there are selected objects or a main object, focus on their combined bounding sphere
                let boundingSphere: { center: ReadonlyVec3; radius: number } | undefined;
                if ((highlighted.current.idArr.length > 0 || mainObject !== undefined) && db && scene) {
                    const focusIds =
                        mainObject !== undefined && !highlighted.current.idArr.includes(mainObject)
                            ? [mainObject, ...highlighted.current.idArr]
                            : highlighted.current.idArr;
                    try {
                        boundingSphere =
                            (await objIdsToTotalBoundingSphere({
                                ids: focusIds,
                                abortSignal,
                                db,
                                view,
                                flip: !vec3.equals(scene.up ?? [0, 1, 0], [0, 0, 1]),
                            })) ?? undefined;
                    } catch (error) {
                        if (!abortSignal.aborted) {
                            console.error("Failed to calculate bounding sphere:", error);
                            telemetry.trackCaughtException({
                                source: "axisSphere.AxisSphere.boundingSphere",
                                category: "render",
                                error,
                                severity: "error",
                            });
                        }
                    }
                }

                if (abortSignal.aborted) {
                    return;
                }

                // If no selection: pick a point near the center of the screen to rotate around
                let viewPickPosition: ReadonlyVec3 | undefined;
                if (!boundingSphere) {
                    const mainCanvasRect = mainCanvas.getBoundingClientRect();
                    const viewPick = await view.pick(mainCanvasRect.width / 2, mainCanvasRect.height / 2, {
                        sampleDiscRadius: 200,
                        searchType: "closestToPickCenter",
                        async: true,
                    });

                    if (abortSignal.aborted) {
                        return;
                    }

                    viewPickPosition = viewPick?.position;
                }

                if (abortSignal.aborted) {
                    return;
                }

                const directionRotation = AXIS_SPHERE_CAMERA_DIRECTIONS[partName]!.rotation;
                const newCameraPos = computeNewCameraPosition({
                    directionRotation,
                    currentPosition: cameraState.position,
                    boundingSphere,
                    viewPickPosition,
                    fov: view.controllers.flight.fov,
                });

                // Highlight the clicked part while camera is flying (pinhole only, ortho is instant)
                if (cameraType === CameraType.Pinhole) {
                    clickedPartNameRef.current = partName;
                    clickedPartHighlightTimerRef.current = setTimeout(() => {
                        clickedPartNameRef.current = null;
                        clickedPartHighlightTimerRef.current = null;
                    }, CAMERA_FLY_TIME);
                }

                void track(Events.axisSphere.faceTapped, { part_name: partName });

                dispatch(
                    renderActions.setCamera({
                        type: cameraState.kind === "pinhole" ? CameraType.Pinhole : CameraType.Orthographic,
                        goTo: {
                            position: newCameraPos,
                            rotation,
                            flyTime: cameraState.kind === "pinhole" ? CAMERA_FLY_TIME : 0,
                        },
                    }),
                );
            }
        },
        [
            dispatch,
            view,
            abort,
            abortController,
            mainCanvas,
            cameraType,
            highlighted,
            mainObject,
            db,
            scene,
            axisSphereConstraints,
        ],
    );

    const endPointerInteraction = useCallback(
        (e: React.PointerEvent<HTMLCanvasElement>, invokeClick: boolean) => {
            const drag = dragStateRef.current;
            dragStateRef.current = null;

            if (isDragging) {
                setIsDragging(false);
            }

            // Clear hover highlight for touch/pen (no mouseLeave event to do it)
            if (e.pointerType !== "mouse" && hoveredMeshPartNameRef.current !== null) {
                hoveredMeshPartNameRef.current = null;
                setIsHoveringClickable(false);
                renderRef.current?.();
            }

            if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                e.currentTarget.releasePointerCapture(e.pointerId);
            }

            if (invokeClick && drag && !drag.isDragging) {
                handleClick(e);
            }
        },
        [handleClick, isDragging],
    );

    const handlePointerUp = useCallback(
        (e: React.PointerEvent<HTMLCanvasElement>) => {
            endPointerInteraction(e, true);
        },
        [endPointerInteraction],
    );

    const handlePointerCancel = useCallback(
        (e: React.PointerEvent<HTMLCanvasElement>) => {
            endPointerInteraction(e, false);
        },
        [endPointerInteraction],
    );

    const handleLostPointerCapture = useCallback(
        (e: React.PointerEvent<HTMLCanvasElement>) => {
            endPointerInteraction(e, false);
        },
        [endPointerInteraction],
    );

    // Highlight meshes on hover
    const handleHoverMove = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
        if (!pickRef.current) {
            return;
        }

        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }

        const rect = canvas.getBoundingClientRect();
        const x = (e.clientX - rect.left) * (canvas.width / rect.width);
        const y = (e.clientY - rect.top) * (canvas.height / rect.height);

        const pickResult = pickRef.current(x, y);
        const newHoveredPartName = pickResult?.partName ?? null;
        let partName = newHoveredPartName && getPartName(newHoveredPartName);

        if (partName && !AXIS_SPHERE_CAMERA_DIRECTIONS[partName]) {
            partName = null;
        }

        // Update cursor based on whether we're hovering a clickable mesh
        const isClickable = partName !== null;
        setIsHoveringClickable(isClickable);

        // Only re-render if hover state changed
        if (hoveredMeshPartNameRef.current !== partName) {
            hoveredMeshPartNameRef.current = partName;
            renderRef.current?.();
        }
    }, []);

    const handlePointerMove = useCallback(
        (e: React.PointerEvent<HTMLCanvasElement>) => {
            const drag = dragStateRef.current;
            if (!drag) {
                handleHoverMove(e);
                return;
            }

            if (!drag.isDragging) {
                const totalDx = e.clientX - drag.startClientX;
                const totalDy = e.clientY - drag.startClientY;
                if (Math.abs(totalDx) < DRAG_THRESHOLD && Math.abs(totalDy) < DRAG_THRESHOLD) {
                    handleHoverMove(e);
                    return;
                }

                if (!drag.orbitPointResolved) {
                    return;
                }

                drag.isDragging = true;
                setIsDragging(true);
                hoveredMeshPartNameRef.current = null;
                setIsHoveringClickable(false);
                renderRef.current?.();
            }

            const dx = e.clientX - drag.startClientX;
            const dy = e.clientY - drag.startClientY;

            const decomposed = decomposeRotation(drag.initialRotation);
            const roll = decomposed.roll;

            // Any active constraint locks pitch so ring drag only changes yaw (compass heading).
            const pitchLocked = !!axisSphereConstraints?.allowedViews;
            const pitch = pitchLocked
                ? decomposed.pitch
                : Math.min(Math.PI / 2, Math.max(-Math.PI / 2, decomposed.pitch - dy * DRAG_SENSITIVITY));
            const yaw = decomposed.yaw - dx * DRAG_SENSITIVITY;
            const newRotation = computeRotation(radToDeg(roll), radToDeg(pitch), radToDeg(yaw));
            quat.normalize(newRotation, newRotation);

            const newCameraDir = getCameraDir(newRotation);

            let newPosition = drag.initialPosition;
            if (drag.orbitPoint) {
                const orbitPoint = drag.orbitPoint;
                const orbitDistance = vec3.dist(drag.initialPosition, orbitPoint);
                newPosition = vec3.scaleAndAdd(vec3.create(), orbitPoint, newCameraDir, -orbitDistance);
            }

            // Modify view directly (not via redux) for better performance
            view?.activeController.moveTo(newPosition, 0, newRotation);
        },
        [view, handleHoverMove, axisSphereConstraints],
    );

    // Remove highlight on mouse leave
    const handleMouseLeave = useCallback(() => {
        if (dragStateRef.current) {
            return;
        }
        setIsHoveringClickable(false);
        if (hoveredMeshPartNameRef.current !== null) {
            hoveredMeshPartNameRef.current = null;
            renderRef.current?.();
        }
    }, []);

    return (
        <div
            style={{
                position: "relative",
                width: size,
                height: size,
            }}
        >
            <canvas
                id="axis-sphere-canvas"
                ref={canvasRef}
                width={size * dpr}
                height={size * dpr}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerCancel}
                onLostPointerCapture={handleLostPointerCapture}
                onMouseLeave={handleMouseLeave}
                style={{
                    width: "100%",
                    height: "100%",
                    background: "transparent",
                    pointerEvents: isPointerDown ? "none" : "auto",
                    cursor: isDragging ? "grabbing" : isHoveringClickable ? "pointer" : "default",
                    touchAction: "none",
                    userSelect: "none",
                }}
            />
        </div>
    );
}

// Get the part name from the mesh name
// E.g. there can be meshes named "part_top_text" and "part_top",
// but the part name is "part_top"
function getPartName(name: string): string {
    return name.endsWith("_text") ? name.slice(0, -5) : name;
}
